import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Bell, Settings, LogOut, ShieldCheck, 
  Search, PanelLeftClose, PanelLeftOpen, Lock, X, RefreshCw, 
  Eye, EyeOff, UserCircle, Edit3, CheckCircle2, User
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";
const APP_NAME = process.env.REACT_APP_APP_NAME || "Sóc Trăng Solution";

const MainLayout = () => {
  // --- STATE QUẢN LÝ GIAO DIỆN ---
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('info'); // 'info' hoặc 'password'
  
  // --- STATE DỮ LIỆU ---
  const [userInfo, setUserInfo] = useState({ name: '', role: '', initials: '', username: '' });
  const [editForm, setEditForm] = useState({ fullname: '', username: '' });
  const [passForm, setPassForm] = useState({ old: '', new: '', confirm: '' });
  
  const [showPass, setShowPass] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false });

  const navigate = useNavigate();
  const location = useLocation();
  const rightPanelRef = useRef(null);

  // 1. Khởi tạo dữ liệu từ LocalStorage
  useEffect(() => {
    const loadData = () => {
      const stored = localStorage.getItem('user_info');
      if (stored) {
        const u = JSON.parse(stored);
        const name = u.fullname || u.username;
        const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        setUserInfo({ ...u, name, initials });
        setEditForm({ fullname: name, username: u.username });
      }
    };
    loadData();
  }, []);

  // 2. Đóng panel phải khi click bên ngoài
  useEffect(() => {
    const handler = (e) => {
      if (isRightPanelOpen && rightPanelRef.current && !rightPanelRef.current.contains(e.target)) {
        if (!e.target.closest('.profile-trigger')) setIsRightPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isRightPanelOpen]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // 3. Hàm cập nhật thông tin cá nhân
  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', isError: false });
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/users/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (!response.ok) throw new Error("Lỗi cập nhật dữ liệu");
      
      const updated = { ...userInfo, fullname: editForm.fullname, username: editForm.username };
      localStorage.setItem('user_info', JSON.stringify(updated));
      const initials = editForm.fullname.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
      setUserInfo({ ...updated, name: editForm.fullname, initials });
      setMessage({ text: 'Cập nhật thông tin thành công!', isError: false });
    } catch (err) {
      setMessage({ text: err.message, isError: true });
    } finally { setIsSubmitting(false); }
  };

  // 4. Hàm cập nhật mật khẩu
  const handleUpdatePass = async (e) => {
    e.preventDefault();
    if (passForm.new !== passForm.confirm) return setMessage({ text: 'Mật khẩu không khớp', isError: true });
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ old_password: passForm.old, new_password: passForm.new })
      });
      if (!response.ok) throw new Error("Mật khẩu cũ không chính xác");
      setMessage({ text: 'Thành công! Đang đăng xuất...', isError: false });
      setTimeout(handleLogout, 2000);
    } catch (err) {
      setMessage({ text: err.message, isError: true });
    } finally { setIsSubmitting(false); }
  };

  const menuItems = [
    { path: '/dashboard', icon: <LayoutDashboard size={22}/>, label: 'Dashboard' },
    { path: '/xml', icon: <FileText size={22}/>, label: 'Hồ sơ XML' },
    { path: '/notifications', icon: <Bell size={22}/>, label: 'Thông báo' },
    { path: '/settings', icon: <Settings size={22}/>, label: 'Cài đặt' }
  ];

  return (
    <div className="h-screen w-full bg-[#f1f5f9] flex overflow-hidden font-sans selection:bg-sky-100">
      
      {/* --- SIDEBAR TRÁI (3D Depth & No-Wrap) --- */}
      <aside className={`bg-white border-r border-slate-200 flex flex-col transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-[10px_0_30px_-15px_rgba(0,0,0,0.05)] z-40 overflow-x-hidden hidden lg:flex ${isSidebarCollapsed ? 'w-20' : 'w-64'}`}>
        <div className="h-20 flex items-center px-6 shrink-0 border-b border-slate-50 overflow-hidden">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 shadow-lg shadow-blue-200 flex items-center justify-center shrink-0">
            <ShieldCheck size={24} className="text-white"/>
          </div>
          <span className={`ml-4 font-black text-slate-800 uppercase tracking-tighter text-sm transition-opacity duration-300 whitespace-nowrap ${isSidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}>
            XML Solution
          </span>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {menuItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <button 
                key={item.path} 
                onClick={() => navigate(item.path)} 
                className={`flex items-center w-full h-12 rounded-xl transition-all duration-300 group relative overflow-hidden active:scale-95 ${isActive ? 'bg-white shadow-[0_10px_20px_-5px_rgba(0,0,0,0.08)] text-sky-600 border border-slate-100' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                <div className={`shrink-0 flex items-center justify-center transition-all duration-300 ${isSidebarCollapsed ? 'w-full' : 'w-12'}`}>
                  {item.icon}
                </div>
                <span className={`text-sm font-bold transition-opacity duration-300 whitespace-nowrap ${isSidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100 ml-1'}`}>
                  {item.label}
                </span>
              </button>
            )
          })}
        </nav>

        <div className="p-4 border-t border-slate-50">
          <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="flex items-center justify-center w-full h-12 rounded-xl bg-slate-50 text-slate-400 hover:text-sky-600 hover:bg-white transition-all duration-300 active:scale-90 shadow-inner">
            {isSidebarCollapsed ? <PanelLeftOpen size={20}/> : <PanelLeftClose size={20}/>}
          </button>
        </div>
      </aside>

      {/* --- NỘI DUNG CHÍNH --- */}
      <main className="flex-1 flex flex-col min-w-0 bg-[#f8fafc] relative">
        <header className="h-16 lg:h-20 flex-none px-6 lg:px-8 flex justify-between items-center bg-white border-b border-slate-200/60 sticky top-0 z-20">
          <h2 className="text-lg lg:text-xl font-black text-slate-800 tracking-tight leading-none">
            {menuItems.find(m => m.path === location.pathname)?.label || 'Workspace'}
          </h2>

          <div className="flex items-center gap-4 lg:gap-6">
            <div className="hidden md:flex items-center gap-3 bg-slate-100/60 px-4 py-2 rounded-xl shadow-inner w-64 focus-within:w-80 transition-all focus-within:bg-white focus-within:ring-2 ring-sky-100">
              <Search size={16} className="text-slate-400" />
              <input type="text" placeholder="Tìm kiếm nhanh..." className="bg-transparent border-none outline-none text-xs w-full text-slate-600 font-medium" />
            </div>

            <button onClick={() => setIsRightPanelOpen(true)} className="profile-trigger flex items-center gap-3 p-1 pr-4 rounded-xl hover:bg-white transition-all active:scale-95 group border border-transparent hover:border-slate-100 shadow-sm hover:shadow-md">
              <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center font-bold text-sm shadow-md group-hover:bg-sky-600 transition-colors duration-500">
                {userInfo.initials}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-black text-slate-800 leading-none">{userInfo.name}</p>
                <p className="text-[10px] font-bold text-slate-400 uppercase mt-1 tracking-widest">{userInfo.role}</p>
              </div>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 lg:p-6 pb-24 lg:pb-6 relative z-0 bg-[#f1f5f9]/30">
          <Outlet /> 
        </div>
      </main>

      {/* --- RIGHT DRAWER (Slim, Square, Mobile Responsive) --- */}
      <div className={`fixed inset-0 z-[100] transition-opacity duration-500 ${isRightPanelOpen ? 'bg-slate-900/30 backdrop-blur-sm opacity-100 pointer-events-auto' : 'bg-transparent opacity-0 pointer-events-none'}`}>
        <aside 
          ref={rightPanelRef} 
          className={`absolute top-0 right-0 h-full w-full lg:max-w-[400px] bg-white shadow-[-8px_0_40px_rgba(0,0,0,0.1)] transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col rounded-none ${isRightPanelOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-slate-50/50 shrink-0">
            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
               <UserCircle size={20} className="text-sky-500"/> Tài khoản
            </h3>
            <button onClick={() => setIsRightPanelOpen(false)} className="p-2 hover:bg-slate-200 rounded-lg text-slate-400 transition-all active:rotate-90">
              <X size={20}/>
            </button>
          </div>

          <div className="flex-1 p-6 lg:p-8 space-y-6 overflow-y-auto custom-scrollbar">
            {/* Tab Selector (Compact) */}
            <div className="flex p-1 bg-slate-100 rounded-xl shadow-inner">
              <button onClick={() => setActiveTab('info')} className={`flex-1 py-2 text-[11px] font-black rounded-lg transition-all ${activeTab === 'info' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500'}`}>THÔNG TIN</button>
              <button onClick={() => setActiveTab('password')} className={`flex-1 py-2 text-[11px] font-black rounded-lg transition-all ${activeTab === 'password' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500'}`}>BẢO MẬT</button>
            </div>

            {message.text && (
              <div className={`p-4 rounded-xl text-[11px] font-bold flex items-center gap-2 animate-in slide-in-from-top-2 ${message.isError ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                {message.isError ? <X size={14}/> : <CheckCircle2 size={14}/>} {message.text}
              </div>
            )}

            {activeTab === 'info' ? (
              <form onSubmit={handleUpdateInfo} className="space-y-5 animate-in fade-in duration-300">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Họ tên hiển thị</label>
                  <div className="relative group">
                    <Edit3 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-sky-500" size={16}/>
                    <input type="text" className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-200 rounded-xl outline-none text-sm font-bold shadow-sm transition-all" value={editForm.fullname} onChange={e => setEditForm({...editForm, fullname: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Username (ID)</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-sky-500" size={16}/>
                    <input type="text" className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-200 rounded-xl outline-none text-sm font-bold shadow-sm transition-all" value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value})} />
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 hover:bg-black text-white py-3.5 rounded-xl font-black text-xs shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                  {isSubmitting ? <RefreshCw className="animate-spin" size={16}/> : 'LƯU THAY ĐỔI'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleUpdatePass} className="space-y-5 animate-in fade-in duration-300">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mật khẩu cũ</label>
                  <input type="password" required className="w-full px-5 py-3 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl outline-none text-sm font-bold shadow-sm" value={passForm.old} onChange={e => setPassForm({...passForm, old: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mật khẩu mới</label>
                  <div className="relative">
                    <input type={showPass ? "text" : "password"} required className="w-full px-5 py-3 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl outline-none text-sm font-bold shadow-sm" value={passForm.new} onChange={e => setPassForm({...passForm, new: e.target.value})} />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-sky-500">
                      {showPass ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Xác nhận</label>
                  <input type="password" required className="w-full px-5 py-3 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl outline-none text-sm font-bold shadow-sm" value={passForm.confirm} onChange={e => setPassForm({...passForm, confirm: e.target.value})} />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-sky-600 hover:bg-sky-700 text-white py-3.5 rounded-xl font-black text-xs shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2">
                  {isSubmitting ? <RefreshCw className="animate-spin" size={16}/> : 'CẬP NHẬT BẢO MẬT'}
                </button>
              </form>
            )}
          </div>

          <div className="p-8 border-t border-slate-100 mt-auto bg-slate-50/30 shrink-0">
            <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-white border border-rose-100 text-rose-500 font-black text-[11px] hover:bg-rose-50 transition-all shadow-sm active:scale-95">
              <LogOut size={16}/> ĐĂNG XUẤT TÀI KHOẢN
            </button>
          </div>
        </aside>
      </div>

      {/* --- MOBILE BOTTOM NAVBAR (Slim Native Indicator) --- */}
      <nav className="lg:hidden fixed bottom-0 left-0 w-full bg-white/90 backdrop-blur-xl border-t border-slate-200 flex justify-around items-center h-16 z-40 px-2 pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button 
              key={item.path} 
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-all relative ${isActive ? 'text-sky-600' : 'text-slate-400'}`}
            >
              <div className={`transition-all duration-300 ${isActive ? 'scale-110 -translate-y-0.5' : 'scale-100'}`}>
                {item.icon}
              </div>
              <span className={`text-[10px] font-bold mt-1 transition-all ${isActive ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
                {item.label}
              </span>
              
              {/* Active indicator: Slim, placed below everything */}
              {isActive && (
                <div className="absolute bottom-1 w-5 h-[2px] bg-sky-600 rounded-full shadow-[0_0_8px_rgba(14,165,233,0.4)]"></div>
              )}
            </button>
          )
        })}
      </nav>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
      
    </div>
  );
};

export default MainLayout;